// Palette visuelle CYBERPUNK — source UNIQUE (elle était dupliquée dans ChapterDetailScreen
// et MissionCardsScreen, avec le risque classique de dérive entre les deux).
export const CYB = {
  bg: '#0a0e1a',
  surface: '#0f1525',
  card: '#131929',
  border: 'rgba(255,255,255,0.08)',
  borderCy: 'rgba(0,240,255,0.3)',
  cyan: '#00f0ff',
  orange: '#ff7a00',
  red: '#ff2d55',
  green: '#00e676',
  purple: '#a855f7',
  textPrim: '#e8eaf6',
  textSec: '#8892a4',
  textMute: '#4a5568',
};

/** Variante CLAIRE de la palette — CCT-279.
 *
 * ⚠️ CE N'EST PAS L'INVERSE DE LA SOMBRE. Un simple inversement donne du blanc sur blanc :
 * les fonds se confondent, et les bordures à 8 % d'opacité — invisibles sur clair —
 * effacent la structure. D'où deux choix explicites :
 *
 *   * un FOND GRIS, pas blanc, et des surfaces plus claires que lui : c'est le contraste
 *     entre les deux qui dessine les cartes, pas une ombre ;
 *   * des BORDURES GRIS MOYEN, opaques. Sur dark une bordure translucide suffit parce que
 *     le fond est très foncé ; sur clair elle disparaît, et l'écran devient un aplat.
 *
 * Les accents néon sont ASSOMBRIS : le cyan `#00f0ff` sur blanc est illisible (contraste
 * ~1,4:1). On garde la teinte — c'est elle qui identifie le type d'élément — en descendant
 * la luminosité jusqu'à un contraste utilisable. */
export const CYB_CLAIR = {
  bg: '#e8ebf0',            // gris de fond, franchement plus foncé que le blanc
  surface: '#f3f5f8',
  card: '#ffffff',
  border: 'rgba(15,23,42,0.34)',    // gris moyen OPAQUE, pas un voile
  borderCy: 'rgba(0,132,150,0.45)',
  cyan: '#0891b2',
  orange: '#c2410c',
  red: '#be123c',
  green: '#047857',
  purple: '#7e22ce',
  textPrim: '#0f172a',
  textSec: '#475569',
  textMute: '#94a3b8',
};

export type Palette = typeof CYB;

/** La palette du thème COURANT. Les écrans importaient `CYB` en dur : la mission restait
 *  dark alors que l'application entière était passée en clair. */
export function palette(dark: boolean): Palette {
  return dark ? CYB : CYB_CLAIR;
}

/** Couleur d'accent par type d'élément — c'est elle qui pilote la surbrillance. */
export const TYPE_COLOR: Record<string, string> = {
  scene: CYB.cyan,
  story: CYB.cyan,
  chat: CYB.orange,
  card: CYB.orange,
  quiz: CYB.purple,
  task: CYB.green,
  incident: CYB.red,
};

/**
 * Barre d'accent GAUCHE DROITE (coins carrés) — design commun des cartes à liseré latéral
 * (notification non lue, quiz, chapitre, user story, callout…). À COMBINER avec
 * `borderLeftWidth`/`borderLeftColor` : sans ça, le liseré épouse le `borderRadius` de la
 * carte et s'arrondit ; on force les coins gauches à 0 pour obtenir une barre RECTILIGNE.
 */
export const squareLeftCorners = { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } as const;

/**
 * SURBRILLANCE « en voile » de la palette : fond teinté + bord lumineux à la couleur de
 * l'élément — jamais un aplat plein. Un aplat (ex. bleu #3B82F6 sur une option de quiz)
 * écrase le contenu, ignore la couleur du type et jure avec le reste de l'interface.
 *
 * `on=false` renvoie l'état neutre (surface + bord discret) pour éviter les ternaires
 * dupliqués sur chaque écran.
 */
// `isDark` (défaut true = ancien comportement, aucune régression) : l'état ACTIF est un aplat
// accent (`${color}1F`), identique en clair comme en dark ; seul l'état NEUTRE (off) suit le
// thème — carte/bord/texte sombres en dark, clairs en clair — pour que les boutons non
// sélectionnés restent lisibles quand l'écran est en thème clair.
const NEUTRAL = {
  dark: { bg: CYB.card, border: CYB.border, text: CYB.textPrim },
  // Le bord NEUTRE suit la palette claire : `#e2e8f0` sur blanc ne se voyait pas, et un bouton
  // non sélectionné devenait un rectangle sans contour.
  light: { bg: CYB_CLAIR.card, border: CYB_CLAIR.border, text: CYB_CLAIR.textPrim },
};
export const glow = (color: string, on: boolean, isDark = true) => {
  const n = isDark ? NEUTRAL.dark : NEUTRAL.light;
  return { backgroundColor: on ? `${color}1F` : n.bg, borderColor: on ? color : n.border };
};

/** Couleur de TEXTE assortie à une surbrillance (accent quand active, texte normal sinon). */
export const glowText = (color: string, on: boolean, isDark = true) =>
  (on ? color : (isDark ? NEUTRAL.dark.text : NEUTRAL.light.text));
